import * as React from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import styled from 'styled-components';
import { NotificationDot } from '../NotificationDot';
import { Size } from '../types';
import { COLORS, focusStyles } from '../styles';
import { Z_INDEXES } from '../styles/z-indexes';
import { defaultOnMouseDownHandler } from '../common';
import { useState } from 'react';

type TabLinkProps = {
  disabled?: boolean;
  forceDeactivate?: boolean;
  requiredLine: string;
  optionalLine?: string;
  OptionalLineIcon?: React.ReactNode; //React.FunctionComponent<BaseProps>;
  endLineIcon?: React.ReactNode;
  onLinkClick?: (e: React.MouseEvent) => void;
  testId?: string;
  onActiveStateChanged?: (state: boolean) => void;
  showNotificationDot?: boolean;
  size?: Size.Small | Size.Medium | Size.Large;
  variant?: 'positive' | 'critical';
  containerOnMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  containerOnMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
} & NavLinkProps;

const OptionalLineWrapperWithIcon = styled.div`
  display: flex;
  flex-direction: row;

  span {
    margin: 0 0 0 4px !important;
    font-size: 12px;
    line-height: 16px;
  }
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: row;

  span:not(:only-child) {
    width: calc(100% - 32px);
  }
`;

const TextContainer = styled.div`
  width: 100%;
  margin: auto 0 auto 16px !important;
`;

const StyledNotification = styled.div`
  margin: 0 15px 0 0 !important;
`;

const EndLineIcon = styled.div`
  margin: 0 15px 0 0 !important;
`;

const OptionalLineWrapper = styled.div`
  font-size: 12px;
`;
//replaced styledTabLink because 'className' and 'styled' functions for some reason were not being executed
const Wrapper = styled.div<{ disabled: boolean }>`
  position: relative;
  &.disabled {
    cursor: not-allowed;

    a {
      pointer-events: none;
    }
  }

  a {
    display: flex;
    flex-direction: row;
    align-items: center;

    font-size: 16px;
    line-height: 120%;
    color: ${COLORS.neutral_600};

    background-color: ${(props) => (props.disabled ? COLORS.neutral_100 : 'transparent')};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    border-left: 1px solid transparent;
    text-decoration: none;
    position: relative;
    border-radius: 2px;

    &:not(:last-child) {
      margin-bottom: 4px;
    }

    &:focus {
      ${focusStyles}
    }

    &.active {
      z-index: ${Z_INDEXES.active};
      background-color: ${COLORS.neutral_20};
    }

    &.active:hover, &.active.dropdown-hover {
      background-color: ${COLORS.primary_20};
    }

    &:hover, &.dropdown-hover {
      z-index: ${Z_INDEXES.hover};
      background-color: ${COLORS.primary_20};
      color: ${COLORS.primary_600};
    }

    &:active {
      z-index: ${Z_INDEXES.active};
      background-color: ${COLORS.primary_100};
      color: ${COLORS.primary_800};
    }

    &.active {
      &::after {
        position: absolute;
        content: ' ';
        width: 4px;
        top: 6px;
        bottom: 6px;
        left: 3px;
        background-color: ${COLORS.primary_500};

        border-radius: 4px;
      }
    }

    &.active:hover, &.active.dropdown-hover {
      &::after {
        background-color: ${COLORS.primary_600};
      }
    }

    &.active:active {
      background-color: ${COLORS.primary_100};

      &::after {
        background-color: ${COLORS.primary_800};
      }
    }

    &.disabled {
      background-color: ${COLORS.white};
      color: ${COLORS.neutral_300};

      span {
        color: ${COLORS.neutral_300};
      }

      &::after {
        background-color: ${COLORS.neutral_300};
      }
    }
  }
`;

const TabLink = React.forwardRef<HTMLAnchorElement, TabLinkProps>(({
  to,
  disabled = false,
  requiredLine,
  optionalLine,
  OptionalLineIcon,
  onLinkClick,
  forceDeactivate,
  testId,
  showNotificationDot = false,
  size = Size.Small,
  endLineIcon,
  onActiveStateChanged,
  variant = 'critical',
  children,
  containerOnMouseEnter,
  containerOnMouseLeave,
  className,
  ...rest
}: TabLinkProps, ref) => {
  
  const [activeState, setActiveState] = useState<boolean>(false);

  return (
    <Wrapper disabled={disabled} className={disabled ? 'disabled' : ''} onMouseEnter={containerOnMouseEnter} onMouseLeave={containerOnMouseLeave}>
      <>
        <NavLink
          to={forceDeactivate ? 'invalid' : to.toString()}
          //disabled={disabled}
          className={({ isActive }) => {
            onActiveStateChanged && onActiveStateChanged(activeState);
            if (forceDeactivate) {
              if (disabled) return 'disabled';
              else return '';
            }

            if (isActive != activeState) setActiveState(isActive);

            return (isActive ? 'active ' : '') + (disabled ? 'disabled' : '');
          }}
          onMouseDown={defaultOnMouseDownHandler}
          tabIndex={disabled ? -1 : 0}
          role="tab"
          aria-selected={activeState}
          onClick={(e: React.MouseEvent) => !disabled && onLinkClick && onLinkClick(e)}
          ref={ref}
          data-testid={testId}
          {...rest}>
          <TextContainer>
            <TopWrapper>
              <span>{requiredLine}</span>
            </TopWrapper>
            {optionalLine && OptionalLineIcon ? (
              <OptionalLineWrapperWithIcon>
                {OptionalLineIcon}
                {!!optionalLine && <span>{optionalLine}</span>}
              </OptionalLineWrapperWithIcon>
            ) : optionalLine ? (
              <OptionalLineWrapper>
                <span>{optionalLine}</span>
              </OptionalLineWrapper>
            ) : (
              <></>
            )}
          </TextContainer>
          <StyledNotification>{showNotificationDot && <NotificationDot testId="NotificationDot" size={size} variant={variant} />}</StyledNotification>
          {endLineIcon && <EndLineIcon>{endLineIcon}</EndLineIcon>}
        </NavLink>
        {children}
      </>
    </Wrapper>
  );
});

export default TabLink;
